import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { listLabUploads } from "@/lib/labs.functions";
import type { LabUploadRow } from "./labTrends";

export function useLabUploads() {
  const list = useServerFn(listLabUploads);
  const [rows, setRows] = useState<LabUploadRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) {
        setRows([]);
        return;
      }
      const r = await list({ data: { accessToken } });
      setRows(Array.isArray(r) ? r : []);
    } catch (e: unknown) {
      // Never log access tokens or raw error objects to the browser console.
      console.warn("listLabUploads failed");
      const msg = e instanceof Error ? e.message : "";
      // Only surface server-sanitized messages; fall back to a generic one.
      setError(msg && msg.length < 200 ? msg : "Couldn't load your lab uploads.");
    } finally {

      setLoading(false);
    }
  }, [list]);

  useEffect(() => {
    let active = true;
    (async () => {
      await fetchRows();
      if (!active) return;
    })();
    return () => { active = false; };
  }, [fetchRows]);

  return { rows, loading, error, refetch: fetchRows };
}
